import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import AppMenu from './AppMenu';
import { MenuProvider } from './context/menucontext';
import { LayoutContext } from './context/layoutcontext';
import impersonationService from '../services/impersonationService';

const AppSidebar = () => {
    const { layoutConfig } = useContext(LayoutContext);
    const [isImpersonating, setIsImpersonating] = useState(false);
    const [impersonatedUser, setImpersonatedUser] = useState(null);
    const [originalAdmin, setOriginalAdmin] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        // Check initial impersonation state
        checkImpersonationState();

        // Subscribe to auth state changes
        const unsubscribe = impersonationService.onAuthStateChange((authState) => {
            setIsImpersonating(authState.isImpersonating);
            setImpersonatedUser(authState.impersonatedUser);
            setOriginalAdmin(authState.originalAdmin);
        });

        // Also check every second to ensure we catch changes
        const interval = setInterval(checkImpersonationState, 1000);

        // Cleanup subscription on unmount
        return () => {
            unsubscribe();
            clearInterval(interval);
        };
    }, []);

    const checkImpersonationState = () => {
        const isImpersonatingNow = impersonationService.isImpersonating();
        const impersonatedUserNow = impersonationService.getImpersonatedUser();
        const originalAdminNow = impersonationService.getOriginalAdmin();

        setIsImpersonating(isImpersonatingNow);
        setImpersonatedUser(impersonatedUserNow);
        setOriginalAdmin(originalAdminNow);
    };

    const handleSwitchBack = async () => {
        setIsLoading(true);
        try {
            await impersonationService.stopImpersonation();
            // Redirect to admin users page
            window.location.href = '/app/admin/users';
        } catch (error) {
            console.error('Failed to switch back:', error);
            alert('Error switching back to admin');
        } finally {
            setIsLoading(false);
        }
    };

    const getSidebarClasses = () => {
        let classes = 'layout-sidebar';
        if (layoutConfig.menuTheme && layoutConfig.menuTheme !== 'colorScheme') {
            classes += ` layout-sidebar-${layoutConfig.menuTheme}`;
        }
        return classes;
    };

    return (
        <div className={getSidebarClasses()} data-menu-theme={layoutConfig.menuTheme}>
            <Link to="/app" className="logo">
                <img src={`${process.env.PUBLIC_URL}/layout/images/interestng_logo_updated.jpeg`} alt="INTERESTNG" className="logo-image" style={{ height: '40px', width: 'auto' }} />
            </Link>

            {/* Impersonation Info and Switch Back Button */}
            {isImpersonating && (
                <div className="p-3" style={{
                    backgroundColor: 'var(--surface-100)',
                    borderBottom: '1px solid var(--surface-border)',
                    marginBottom: '0.5rem'
                }}>
                    <div className="flex flex-column gap-2">
                        <div className="flex align-items-center gap-2 text-sm">
                            <i className="pi pi-user-edit text-orange-500"></i>
                            <span className="font-medium text-600">Viewing as:</span>
                        </div>
                        <div className="text-sm font-semibold text-900">
                            {impersonatedUser?.name || 'Unknown User'}
                        </div>
                        {originalAdmin && (
                            <div className="text-xs text-500">
                                Admin: {originalAdmin.name}
                            </div>
                        )}
                        <Button
                            label={isLoading ? "Switching..." : "Switch Back to Admin"}
                            icon={isLoading ? undefined : "pi pi-arrow-left"}
                            onClick={handleSwitchBack}
                            disabled={isLoading}
                            className="p-button-warning p-button-sm w-full"
                            size="small"
                        >
                            {isLoading && <ProgressSpinner style={{ width: '16px', height: '16px' }} />}
                        </Button>
                    </div>
                </div>
            )}

            <nav className="layout-menu-container" aria-label="Main navigation">
                <MenuProvider>
                    <AppMenu />
                </MenuProvider>
            </nav>
        </div>
    );
};

export default AppSidebar;